import formatPrice from './utils-format-price.js';
import priceHTML from './utils-price-html.js';
import { d as debounce } from './debounce-CFtxQv9K.js';

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.sectionId = this.dataset.sectionId;
    this.quantityInput = this.form.querySelector('input[name="quantity"]');
    this.variantIdInput = this.form.querySelector('input[name="id"]');

    // Get all submit buttons that are part of the form
    this.submitButtons = document.querySelectorAll(
      `button[form="${this.form.getAttribute('id')}"]`
    );

    // Get all bundle parent variants
    this.bundleParentVariants = this.dataset.bundleParentVariants.split(',') || [];

    // State for the main product price and bundle items
    this.setMainProduct();
    this.bundleItems = [];
    this.addOnItems = [];
    this.url = window.location.href.replace(window.location.origin, '');

    // Listen to form submit
    this.form.addEventListener('submit', (event) => this.onFormSubmit(event));

    // Debounce the update price function
    this.debouncedUpdatePrice = debounce(this.updatePrice, 100, { });
    this.debouncedUpdateDeliveryTimes = debounce(this.updateDeliveryTimes, 100, { });

    // Listen to variant changes
    this.unsubscribeVariantChange = subscribe(
      `variant:changed:${this.dataset?.sectionId}`,
      ({ html }) => {
        const productForm = html.querySelector('product-form');
        const productOptions = productForm.querySelector('[data-product-options]');
        if (productOptions) {
          this.setMainProduct(productForm);
          this.querySelector('[data-product-options]').replaceWith(productOptions);
          this.debouncedUpdatePrice();
          this.debouncedUpdateDeliveryTimes();
        } else {
          this.replaceWith(productForm);
        }
      }
    );

    // Listen to product changes
    this.unsubscribeProductChange = subscribe(`product:changed:${this.sectionId}`, ({ html }) => {
      const productForm = html.querySelector('product-form');
      const productOptions = productForm.querySelector('[data-product-options]');
      if (productOptions) {
        this.setMainProduct(productForm);
        this.querySelector('[data-product-options]').replaceWith(productOptions);
        this.debouncedUpdatePrice();
        this.debouncedUpdateDeliveryTimes();
      } else {
        this.replaceWith(productForm);
      }
    });

    // Listen to bundle variant changes
    this.unsubscribeBundleVariantChange = subscribe(
      `bundle:variant:changed:${this.sectionId}`,
      ({ variant, group, noneOption, deliveryTimes }) => {
        this.setBundleItems(variant, group, noneOption, deliveryTimes);
        this.debouncedUpdatePrice();
        this.debouncedUpdateDeliveryTimes();
      }
    );

    // Listen to add-on variant changes
    this.unsubscribeAddOnVariantChange = subscribe(
      `add-on:variant:changed:${this.sectionId}`,
      ({ variant, group, noneOption }) => {
        this.setAddOnItems(variant, group, noneOption);
        this.debouncedUpdatePrice();
      }
    );

    // Listen to quantity changes
    this.unsubscribeQuantityChange = subscribe('quantity:change', ({ quantity }) => {
      this.quantityInput.value = quantity;
    });
  }

  disconnectedCallback() {
    this.unsubscribeVariantChange();
    this.unsubscribeProductChange();
    this.unsubscribeBundleVariantChange();
    this.unsubscribeAddOnVariantChange();
    this.unsubscribeQuantityChange();
    this.debouncedUpdatePrice.cancel();
    this.debouncedUpdateDeliveryTimes.cancel();
  }

  /**
   * Handle form submission
   * @param {Event} event
   */
  async onFormSubmit(event) {
    event.preventDefault();
    this.toggleLoading(true);

    try {
      const { itemComponents, bundleId, bundleConfig, bundleImage } = await this.getBundle();
      const { addOnItems } = await this.getAddOnItems();
      const formData = this.createFormData(
        itemComponents,
        bundleId,
        bundleConfig,
        bundleImage,
        addOnItems
      );
      await this.addToCart(formData);
      this.onSuccess();
    } catch (error) {
      this.onError(error);
    } finally {
      this.toggleLoading(false);
    }
  }

  /**
   * Toggle loading state on submit buttons
   * @param {boolean} loading
   */
  toggleLoading(loading) {
    this.submitButtons.forEach((button) => button.classList.toggle('loading', loading));
  }

  /**
   * Update the main product
   * @param {Node} productForm
   * @returns {void}
   */
  setMainProduct(productForm = this) {
    // Get the properties from the subgroup
    const properties = [
      ...productForm.querySelectorAll('[data-subgroup].group-option-selected'),
    ].reduce((acc, item) => {
      const subgroup = item.dataset.subgroup.split('||');
      return { ...acc, [subgroup[0]]: subgroup[1] };
    }, {});

    // Set the main product
    this.mainProduct = {
      price: parseFloat(productForm.querySelector('[data-price]')?.dataset.price || 0),
      compareAtPrice: parseFloat(
        productForm.querySelector('[data-compare-at-price]')?.dataset.compareAtPrice || 0
      ),
      title: productForm?.dataset.mainTitle || 'Bundle',
      fallbackImage: productForm?.dataset.bundleFallbackImage || null,
      bundleImageLogic: JSON.parse(productForm?.dataset.bundleImagesLogic || '[]'),
      variantId: productForm.querySelector('[data-main-variant-id]').value,
      quantity: parseFloat(productForm.querySelector('[data-quantity]').value || 1),
      available: !productForm.querySelector('[data-add-to-cart-button]').hasAttribute('disabled'),
      deliveryTimes: JSON.parse(
        productForm.querySelector('[data-delivery-times]')?.dataset?.deliveryTimes || null
      ),
      properties,
    };

    // Update the variant id and quantity input value
    this.variantIdInput.value = this.mainProduct.variantId;
    this.quantityInput.value = this.mainProduct.quantity;

    // Remove the data-price and data-compare-at-price attributes
    productForm.querySelector('[data-price]').removeAttribute('data-price');
    productForm.querySelector('[data-compare-at-price]').removeAttribute('data-compare-at-price');
  }

  /**
   * Update bundle items
   * @param {object} variant
   * @param {string} group
   * @param {boolean} noneOption
   * @param {object} deliveryTimes
   * @param {number} quantity
   * @returns {void}
   */
  setBundleItems(
    variant,
    group = 'bundle',
    noneOption = false,
    deliveryTimes = null,
    quantity = 1
  ) {
    // Remove the item from the same group
    this.bundleItems = this.bundleItems.filter((item) => item.group !== group);

    // Return if 'none option' is selected, if no variant is found we should show a sold out message
    if (noneOption) {
      return;
    }

    // Add the bundle item to the bundle items array or set the item to null if no variant is found
    const item = {
      group,
      variant,
      id: variant?.id || null,
      quantity,
      price: variant?.price || null,
      compareAtPrice: variant?.compare_at_price || null,
      available: variant?.available || false,
      deliveryTimes,
    };

    this.bundleItems = [...this.bundleItems, item];
  }

  /**
   * Update add-on items
   * @param {object} variant
   * @param {string} group
   * @param {boolean} noneOption
   * @param {number} quantity
   * @returns {void}
   */
  setAddOnItems(variant, group = 'add-on', noneOption = false, quantity = 1) {
    // Remove the item from the same group
    this.addOnItems = this.addOnItems.filter((item) => item.group !== group);

    // Return if 'none option' is selected, if no variant is found we should show a sold out message
    if (noneOption) {
      return;
    }

    // Add the bundle item to the bundle items array or set the item to null if no variant is found
    const item = {
      group,
      variant,
      id: variant?.id || null,
      quantity,
      price: variant?.price || null,
      compareAtPrice: variant?.compare_at_price || null,
      available: variant?.available || false,
    };

    this.addOnItems = [...this.addOnItems, item];
  }

  /**
   * Get the bundle image
   * @returns {string}
   */
  getBundleImage() {
    const logic = this.mainProduct.bundleImageLogic || [];

    const imageLogic = logic.find((item) => {
      const mediaLogic = item.logic;

      // Check if one of the logic values is in the bundle items or add-on items, else hidden is false
      const mediaLogicMap = mediaLogic.map((logic) => {
        let visible = false;
        if (logic.type === 'bundle') {
          logic.values.map((value) => {
            this.bundleItems.map((item) => {
              if (item.variant.options.includes(value)) {
                visible = true;
              }
            });
          });
        } else if (logic.type === 'add-on') {
          logic.values.map((value) => {
            this.addOnItems.map((item) => {
              if (item.variant.options.includes(value)) {
                visible = true;
              }
            });
          });
        }
        return {
          ...logic,
          visible,
        };
      });

      const useImage = mediaLogicMap.every((logic) => logic.visible);

      if (!useImage) {
        return null;
      }

      return item;
    });

    return imageLogic?.image || this.mainProduct.fallbackImage;
  }

  /**
   * Get a new bundle ID by checking which bundle variants are already used in the cart
   * Update the bundle config with the new bundle parent title and image
   * @param {string} bundleImage
   * @returns {{bundleId: number, bundleConfig: Object}} Object containing the bundle ID and config
   */
  async getBundleIdAndConfig(bundleImage) {
    try {
      // Return if no bundle parent variants are set
      if (!this.bundleParentVariants.length) {
        return { bundleId: null, bundleConfig: null };
      }

      // Get the current cart
      const response = await fetch(`${routes.cart_url}.js`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.message || 'Failed to get cart');

      // Get the attributes from the cart
      const attributes = cart.attributes;

      // Get the bundle ID from the cart
      const bundleConfig = attributes._bundle_config ? JSON.parse(attributes._bundle_config) : {};

      // Check if each bundle parent variant is used in the cart else remove it from the bundle config
      Object.keys(bundleConfig).forEach((variantId) => {
        const variant = cart.items.find((item) => parseInt(item.id) === parseInt(variantId));
        if (!variant) {
          delete bundleConfig[variantId];
        }
      });

      // Get a new bundle ID by checking which bundle variants are already used in the cart
      const bundleId = this.bundleParentVariants.find((variantId) => !bundleConfig[variantId]);

      // Return if no new bundle ID is found
      if (!bundleId) {
        return { bundleId: null, bundleConfig: null };
      }

      // Add the bundle parent title and image to the bundle config
      bundleConfig[bundleId] = {
        ...(this.mainProduct.title && { title: this.mainProduct.title }),
        ...(bundleImage && { image: bundleImage }),
      };

      // Return the bundle ID and config
      return { bundleId, bundleConfig };
    } catch (error) {
      this.onError(error);
    }
  }

  /**
   * Get bundle items, bundle ID and bundle config
   * @returns {{itemComponents: Array<{id: string, quantity: number, properties: {bundleId: number}}>, bundleId: number|null}} Object containing array of item components and the bundle ID used to create the bundle inside Shopify Functions
   */
  async getBundle() {
    // Return if there are no bundle items in the array
    if (this.bundleItems.length === 0) {
      return { itemComponents: [], bundleId: null };
    }

    // Get the bundle image
    const bundleImage = this.getBundleImage();

    // Get a new bundle ID
    const { bundleId, bundleConfig } = await this.getBundleIdAndConfig(bundleImage);

    // Return if no bundle ID or config is found
    if (!bundleId || !bundleConfig) {
      return { itemComponents: [], bundleId: null };
    }

    // Return the selected bundle items and the bundle ID
    return {
      itemComponents: this.bundleItems
        .map((item) => ({
          id: item.id,
          quantity: item.quantity,
          properties: {
            _bundle_id: bundleId,
            _main: false,
            _config: this.url,
          },
        }))
        .reverse(),
      bundleId,
      bundleConfig,
      bundleImage,
    };
  }

  /**
   * Get add on items
   * @returns {{addOnItems: Array<{id: string, quantity: number, properties: {bundleId: number}}>}} Object containing array of item components
   */
  getAddOnItems() {
    // Return if there are no bundle items in the array
    if (this.addOnItems.length === 0) {
      return { addOnItems: [] };
    }

    // Return the selected bundle items and the bundle ID
    return {
      addOnItems: this.addOnItems
        .map((item) => ({
          id: item.id,
          quantity: item.quantity,
        }))
        .reverse(),
    };
  }

  /**
   * Create form data
   * @param {Array} itemComponents - Extra items that will be added to the bundle
   * @param {number} bundleId - The bundle ID to use for the item components to create the bundle inside Shopify Functions
   * @param {Object} bundleConfig - The bundle config to use for the item components to create the bundle inside Shopify Functions
   * @param {string} bundleImage - The bundle image to use for the item components to create the bundle inside Shopify Functions
   * @param {Array} addOnItems - The add on items to be added to the cart
   * @returns {Object}
   */
  createFormData(itemComponents, bundleId, bundleConfig, bundleImage, addOnItems) {
    return {
      ...(bundleConfig && {
        attributes: {
          _bundle_config: JSON.stringify(bundleConfig),
        },
      }),
      items: [
        ...addOnItems,
        ...itemComponents,
        {
          id: this.variantIdInput.value,
          quantity: this.quantityInput.value,
          properties: {
            ...(bundleId && { _bundle_id: bundleId, _main: true, _config: this.url }),
            ...(bundleImage && { _main_image: bundleImage }),
            ...(this.mainProduct.properties && { ...this.mainProduct.properties }),
          },
        },
      ],
    };
  }

  /**
   * Add to cart
   * @param {Object} formData
   * @returns {Object}
   */
  async addToCart(formData) {
    try {
      const response = await fetch(`${routes.cart_add_url}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to add to cart');
      }
      return data;
    } catch (error) {
      throw new Error(error || 'Failed to add to cart');
    }
  }

  /**
   * Update the price whit the main, bundle and add one products
   */
  updatePrice() {
    const priceElement = this.querySelector('product-price');
    const extraItems = [...this.bundleItems, ...this.addOnItems];
    let { price, compareAtPrice, available } = this.mainProduct;
    if (!compareAtPrice) {
      compareAtPrice = price;
    }

    // Update the price with the bundle items and add-on items
    extraItems.forEach((item) => {
      price += item.price * item.quantity;
      if (item.compareAtPrice) {
        compareAtPrice += item.compareAtPrice * item.quantity;
      } else {
        compareAtPrice += item.price * item.quantity;
      }
      if (!item.available) {
        available = false;
      }
    });

    // Calculate the difference and sale percentage
    const difference = compareAtPrice - price;
    const salePercentage = Math.round(((compareAtPrice - price) / compareAtPrice) * 100);

    // Update the price element
    if (priceElement?.querySelector('[data-price-element]')) {
      priceElement.querySelector('[data-price-element]').innerHTML = priceHTML(
        price,
        compareAtPrice
      );
    }
    if (priceElement?.querySelector('[data-price-difference]')) {
      priceElement.querySelector('[data-price-difference]').innerHTML =
        `${window.product_translations.you_save} ${formatPrice(difference)}`;
      priceElement
        .querySelector('[data-price-difference]')
        .classList.toggle('hidden', difference <= 0);
    }
    if (priceElement?.querySelector('[data-price-sale-percentage]')) {
      priceElement.querySelector('[data-price-sale-percentage]').innerHTML = `-${salePercentage}%`;
      priceElement
        .querySelector('[data-price-sale-percentage]')
        .classList.toggle('!hidden', salePercentage <= 0);
    }

    // Update the add to cart button
    if (this.querySelector('[data-add-to-cart-button]')) {
      if (!available) {
        this.querySelector('[data-add-to-cart-button]').textContent =
          window.product_translations.sold_out;
        this.querySelector('[data-add-to-cart-button]').setAttribute('disabled', 'disabled');
      } else {
        this.querySelector('[data-add-to-cart-button]').textContent =
          window.product_translations.add_to_cart;
        this.querySelector('[data-add-to-cart-button]').removeAttribute('disabled');
      }
    }
  }

  /**
   * Update the price whit the main, bundle and add one products
   */
  updateDeliveryTimes() {
    const deliveryTimesElement = this.querySelector('[data-delivery-times]');
    const { deliveryTimes } = this.mainProduct;
    const extraItems = [...this.bundleItems]; // Add-on delivery times are not included on the product page
    let fromPrice = 0,
      tillPrice = 0,
      description = '';

    // If main delivery times
    if (deliveryTimes) {
      fromPrice = deliveryTimes.from;
      tillPrice = deliveryTimes.till;
      description = deliveryTimes.description;
    }

    // Update the delivery times with the bundle items (add-on items are not included on the product page)
    extraItems.forEach((item) => {
      if (!item.deliveryTimes) {
        return;
      }
      if (item.deliveryTimes.from > fromPrice || fromPrice === 0) {
        fromPrice = item.deliveryTimes.from;
      }
      if (item.deliveryTimes.till > tillPrice || tillPrice === 0) {
        tillPrice = item.deliveryTimes.till;
        description = item.deliveryTimes.description || description;
      }
    });

    // Hide if no delivery time is set
    if (fromPrice <= 0 && tillPrice <= 0) {
      deliveryTimesElement.classList.add('hidden');
      return;
    }

    // Calculate the week delivery time
    let weekFromPrice = Math.floor(fromPrice / 7);
    let weekTillPrice = Math.floor(tillPrice / 7);

    // Set the delivery message in days or weeks
    let deliveryMessage = '';
    if (weekFromPrice <= 0 && weekTillPrice <= 0) {
      // Make sure the delivery time is at least 1 day
      fromPrice = Math.max(1, fromPrice);
      tillPrice = Math.max(1, tillPrice);
      deliveryMessage = window.product_translations.delivery_time_days
        .replace('{{ from }}', fromPrice)
        .replace('{{ till }}', tillPrice);
    } else {
      // Make sure the delivery time is at least 1 week
      weekFromPrice = Math.max(1, weekFromPrice);
      weekTillPrice = Math.max(1, weekTillPrice);
      deliveryMessage = window.product_translations.delivery_time_weeks
        .replace('{{ from }}', weekFromPrice)
        .replace('{{ till }}', weekTillPrice);
    }

    // Update the delivery times element
    if (deliveryTimesElement) {
      deliveryTimesElement.classList.toggle('hidden', fromPrice <= 0 && tillPrice <= 0);
      deliveryTimesElement.querySelector('[data-delivery-message]').innerHTML = deliveryMessage;
      deliveryTimesElement.querySelector('[data-delivery-description]').innerHTML = description;
    }
  }

  /**
   * Handle form success
   */
  onSuccess() {
    publish('cart:added', {
      productId: this.dataset.productId,
      variantId: this.variantIdInput.value,
    });

    if (
      window.theme.add_to_cart_behavior === 'cart_page' &&
      window.location.pathname !== window.routes.cart_url
    ) {
      window.location.href = window.routes.cart_url;
    } else {
      window.toast.success(window.toast_translations.cart.success);
    }
  }

  /**
   * Handle form error
   * @param {Error} error - The error object
   */
  onError({ message }) {
    console.error(message);
    window.toast.error(message);
  }
}

customElements.define('product-form', ProductForm);
